import {
  BodyText,
  Flex,
  FlexProps,
  Heading,
} from '@utilitywarehouse/hearth-react';
import { color, semantic } from '@utilitywarehouse/hearth-tokens/browser';
import { clsx } from 'clsx';
import { ReactNode } from 'react';

import {
  SlideHeading,
  SlideSubHeading,
  Terms,
} from '@components/PresentationRebrand/ContentSlide/slides/shared';

import './styles.scss';

type CellProps = Pick<
  FlexProps,
  'direction' | 'gap' | 'backgroundColor' | 'alignItems' | 'justifyContent'
> & {
  children: ReactNode;
  borderBottom?: { mobile?: boolean; tablet?: boolean };
  className?: string;
};

type PlusProps = {
  className?: string;
};

type BonusItemProps = {
  src: string;
  alt: string;
  heading: string;
};

type ConditionItemProps = {
  heading: string;
  body: string;
};

type PayoutItemProps = {
  className?: string;
  heading: string;
  body: string;
};

const Cell = ({
  direction = 'column',
  gap,
  backgroundColor,
  justifyContent = 'center',
  alignItems = 'center',
  children,
  borderBottom = {},
  className,
}: CellProps) => (
  <Flex
    direction={direction}
    justifyContent={justifyContent}
    alignItems={alignItems}
    gap={gap}
    paddingX="100"
    paddingY="200"
    alignSelf="stretch"
    backgroundColor={backgroundColor}
    className={clsx(
      'welcome-bonus__grid__cell',
      borderBottom?.mobile
        ? 'welcome-bonus__grid__cell--mobile-border-bottom'
        : undefined,
      borderBottom.tablet
        ? 'welcome-bonus__grid__cell--tablet-border-bottom'
        : undefined,
      className
    )}
  >
    {children}
  </Flex>
);

const Plus = ({ className }: PlusProps) => (
  <span className={clsx('welcome-bonus__plus', className)}>+</span>
);

const BonusItem = ({ heading, src, alt }: BonusItemProps) => (
  <>
    <img src={src} alt={alt} className="welcome-bonus__bonus-item" />
    <Heading size="sm" textAlign="center">
      {heading}
    </Heading>
  </>
);

const ConditionItem = ({ heading, body }: ConditionItemProps) => (
  <>
    <Heading size="sm" textAlign="center">
      {heading}
    </Heading>
    <BodyText textAlign="center">{body}</BodyText>
  </>
);

const PayoutItem = ({ className, heading, body }: PayoutItemProps) => (
  <Flex
    direction="column"
    justifyContent="center"
    alignItems="center"
    className={clsx('welcome-bonus__payout-item', className)}
  >
    <Heading className="welcome-bonus__payout-item__header">{heading}</Heading>
    <BodyText textAlign="center">{body}</BodyText>
  </Flex>
);

export const WelcomeBonus = () => (
  <Flex direction="column" gap="200" className="welcome-bonus">
    <SlideHeading>A warm welcome when you join</SlideHeading>

    <SlideSubHeading>
      Add the UW Cashback Card and get up to £250 loaded onto your card to spend
      your way.
    </SlideSubHeading>

    <div className="welcome-bonus__grid">
      <Flex
        direction="column"
        overflow="hidden"
        className="welcome-bonus__grid__column welcome-bonus__grid__column--start"
      >
        <Cell
          direction={{ mobile: 'row', tablet: 'column' }}
          gap="200"
          backgroundColor={color.cashbackLilac['500']}
          borderBottom={{ mobile: true, tablet: true }}
        >
          <BonusItem
            src="/Presentation/WelcomeBonus/50.svg"
            alt="£50"
            heading="Welcome Bonus"
          />
        </Cell>

        <Cell
          gap="50"
          backgroundColor={color.cashbackLilac['300']}
          borderBottom={{ mobile: true, tablet: true }}
        >
          <ConditionItem
            heading="Pick 2 eligible services"
            body="(like energy, mobile, or broadband)"
          />
        </Cell>

        <Cell
          gap="50"
          backgroundColor={semantic.surface.neutral.strong}
          direction={{ mobile: 'column', tablet: 'row' }}
          alignItems={{ mobile: 'center', tablet: 'start' }}
          justifyContent={{ mobile: 'center', tablet: 'start' }}
          borderBottom={{ tablet: true }}
        >
          <PayoutItem heading="£50" body="loaded following activation" />
        </Cell>
      </Flex>

      <Flex
        direction="column"
        overflow="hidden"
        className="welcome-bonus__grid__column welcome-bonus__grid__column--middle"
      >
        <Cell
          direction={{ mobile: 'row', tablet: 'column' }}
          gap="200"
          backgroundColor={color.cashbackLilac['500']}
          borderBottom={{ mobile: true, tablet: true }}
        >
          <BonusItem
            src="/Presentation/WelcomeBonus/150.svg"
            alt="£150"
            heading="Welcome Bonus"
          />
        </Cell>

        <Cell
          gap="50"
          backgroundColor={color.cashbackLilac['300']}
          borderBottom={{ mobile: true, tablet: true }}
        >
          <ConditionItem
            heading="Pick 3 eligible services"
            body="(like energy, mobile, or broadband)"
          />
        </Cell>

        <Cell
          gap="50"
          backgroundColor={semantic.surface.neutral.strong}
          direction={{ mobile: 'column', tablet: 'row' }}
          alignItems={{ mobile: 'center', tablet: 'start' }}
          justifyContent={{ mobile: 'center', tablet: 'start' }}
          borderBottom={{ tablet: true }}
        >
          <PayoutItem heading="£50" body="loaded following activation" />
          <Plus />
          <PayoutItem heading="£50" body="loaded 30 days later" />
          <Plus />
          <PayoutItem heading="£50" body="loaded a further 30 days later" />
        </Cell>
      </Flex>

      <Flex
        direction="column"
        overflow="hidden"
        className="welcome-bonus__grid__column welcome-bonus__grid__column--end"
      >
        <Cell
          direction={{ mobile: 'row', tablet: 'column' }}
          gap="200"
          backgroundColor={color.cashbackLilac['500']}
          borderBottom={{ mobile: true }}
        >
          <BonusItem
            src="/Presentation/WelcomeBonus/250.svg"
            alt="£250"
            heading="Welcome Bonus"
          />
        </Cell>

        <Cell
          gap="50"
          backgroundColor={color.cashbackLilac['300']}
          borderBottom={{ mobile: true }}
        >
          <ConditionItem
            heading="Pick 4 eligible services"
            body="(like energy, mobile, broadband and boiler & home cover)"
          />
        </Cell>

        <Cell
          gap={{ mobile: '50', tablet: '150', wide: '50' }}
          backgroundColor={semantic.surface.neutral.strong}
          direction={{ mobile: 'column', wide: 'row' }}
          alignItems={{ mobile: 'center', tablet: 'start' }}
          justifyContent={{ mobile: 'center', tablet: 'start' }}
        >
          <div className="welcome-bonus__payout-row">
            <PayoutItem heading="£50" body="loaded following activation" />
            <Plus />
            <PayoutItem heading="£50" body="loaded 30 days later" />
            <Plus />
            <PayoutItem heading="£50" body="loaded a further 30 days later" />
          </div>

          <Plus className="welcome-bonus__plus--no-tablet" />

          <div className="welcome-bonus__payout-row">
            <PayoutItem heading="£50" body="loaded a further 30 days later" />

            <Plus className="welcome-bonus__plus--md-only" />
            <PayoutItem
              className="welcome-bonus__payout-item--md-only"
              heading="£50"
              body="loaded a further 30 days later"
            />
          </div>
        </Cell>
      </Flex>
    </div>

    <Terms />
  </Flex>
);
